import i18next from "i18next";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowIcon from "../../../../assets/images/icons/ProfileIcons/arrow1.svg";
import CalendarIcon from "../../../../assets/images/icons/ProfileIcons/calendar.svg";
import TimeIcon from "../../../../assets/images/icons/ProfileIcons/time2.svg";
import { GeneralButton, Table } from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import { IMedicalRecordItem } from "../../../../interface/MedicalRecord";
import { MedicalRecordStep } from "../../PatientMedicalRecords/MedicalRecordDetails/MedicalRecordStep";
import { recordTablesData } from "../../PatientMedicalRecords/recordDetailsData";
import { MedicalKeys } from "../../../../enums/MedicalReportEnums";
import { useGetData, usePostData } from "../../../../hooks";
import { handlePrint } from "../../../../utils/fileUtils";
import Ic_print from "../../../../assets/images/icons/print_black.svg";

export const RecordCardType = {
  FollowUp: "follow_ups",
  MedicalReport: "medical_report",
} as const;

interface RecordCardItemProps {
  type: (typeof RecordCardType)[keyof typeof RecordCardType];
  item: IMedicalRecordItem;
  openItemId?: number;
  setOpenItemId?: any;
}
const RecordCardItem: React.FC<RecordCardItemProps> = ({
  type,
  item,
  openItemId,
  setOpenItemId,
}) => {
  const [isPrinting, setIsPrinting] = useState(false);

  moment.locale(i18next?.language);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useGetData<any>(
    EndPoints.GetFollowUpById,
    {
      enabled: openItemId == item?.Id,
    },
    item?.Id,
  );
  const TreatmentsData = data?.Data?.Treatments;
  const drugsTable: any = recordTablesData?.find(
    (item) => item?.key == MedicalKeys.Medications,
  );
  const { mutate, isPending } = usePostData(
    `${EndPoints.PrintPrescription}/${TreatmentsData?.Id}`,
  );

  return (
    <div
      className={`w-full cursor-pointer rounded-lg border-[3px] border-primary-bg bg-Gray px-4 py-2 
        ${openItemId == item?.Id ? "!border-primary" : ""} 
        ${type === RecordCardType.MedicalReport ?
          "hover:border-primary hover:bg-primary-click" : ""}`}
    >
      <div
        className={`flex justify-between`}
        onClick={() => {
          type === RecordCardType.MedicalReport
            ? navigate(`${item?.Id}`)
            : openItemId == item?.Id
              ? setOpenItemId(undefined)
              : setOpenItemId(item?.Id);
        }}
      >
        <div className="flex gap-4">
          <div className="rounded-lg border border-radio bg-white px-2 py-0.5 text-xs">
            {`${t(type)} #${item?.Index}`}
          </div>
          <div className="flex items-center gap-2">
            <img className="w-4" src={CalendarIcon} alt="date" />
            <p className="text-sm">{item?.Date}</p>
          </div>
          {item?.Time && (
            <div className="flex items-center gap-2">
              <img className="w-4" src={TimeIcon} alt="time" />
              <p className="text-sm">{item?.Time}</p>
            </div>
          )}
        </div>
        <img
          src={ArrowIcon}
          className={`${i18next?.language === "ar" ? "rotate-180" : ""}`}
          alt="arrow"
        />
      </div>

      {openItemId == item?.Id &&
        (isLoading ? (
          <div className="mt-2">
            <div className="mb-2 h-20 w-full animate-pulse rounded-md bg-gray-200" />
            <div className="mb-2 h-24 w-full animate-pulse rounded-md bg-gray-200" />
          </div>
        ) : (
          <div className="overflow-auto">
            <div className={`flex flex-col gap-4`}>
              {TreatmentsData && (
                <div className="my-2 rounded-lg border border-[#919EAB33] bg-Gray p-6">
                  <div className="flex justify-between">
                    <h2 className="font-bold">
                      {t("treatment_plan")} - {TreatmentsData?.CreatedAt}
                    </h2>

                    {TreatmentsData?.Drugs?.length > 0 && (
                      <GeneralButton
                        title={t("print")}
                        color="lite"
                        className="border border-body-text !bg-white !text-primary-text"
                        icon={Ic_print}
                        isLoading={isPending && isPrinting}
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsPrinting(true);
                          mutate(
                            {},
                            {
                              onSuccess: (data) => {
                                handlePrint(
                                  data?.Data?.FileContents,
                                  data?.Data?.FileDownloadName,
                                  setIsPrinting,
                                );
                              },
                            },
                          );
                        }}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {TreatmentsData?.TreatmentPlan && (
                      <>
                        <h2>{t("treatment_plan")}</h2>
                        <div
                          className="bg-white p-1"
                          dangerouslySetInnerHTML={{
                            __html: TreatmentsData?.TreatmentPlan,
                          }}
                        />
                      </>
                    )}
                    <div className="mt-2 flex flex-col gap-1">
                      <h2>{t("treatments")}</h2>
                      <div
                        className={`overflow-auto rounded-lg bg-Gray ${drugsTable?.columns?.length > 3 ? "col-span-2" : ""}`}
                      >
                        <Table
                          headerTitle={t(drugsTable?.title)}
                          data={TreatmentsData?.Drugs}
                          columns={drugsTable?.columns}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                {recordTablesData?.map((table, index) => {
                  return data?.Data?.[table?.key] ? (
                    <div
                      key={index}
                      className={`rounded-lg border border-[#919EAB33] bg-Gray p-6 ${table?.columns?.length > 3 ? "col-span-2" : ""}`}
                    >
                      <Table
                        headerTitle={t(table?.title)}
                        data={data?.Data?.[table?.key]}
                        columns={table?.columns}
                      />
                    </div>
                  ) : (
                    <></>
                  );
                })}
              </div>
              <div className="flex flex-col gap-2">
                {data?.Data?.Attributes?.map((step: any, index: number) => {
                  return (
                    step?.IsVisible && (
                      <MedicalRecordStep
                        step={step}
                        key={step.id || step.stepId || `step-${index}`}
                        data={data?.Data}
                      />
                    )
                  );
                })}
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

export default RecordCardItem;
